import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { GAMES } from '../constants/games';
import { syncAllUsersToAlgolia } from '../services/userAlgoliaService';
import { runApartmentsAndLegacyMigration, confirmGroupsAndMembershipStructure } from '../services/migrationService';
import { deleteVenueBlockForMatch } from '../services/venueBlockService';
import { isAdminRole } from '../constants/roles';
import { useAlert } from '../contexts/AlertContext';
import './AdminPanel.css';

const CONFIG_ENABLED_GAMES = 'enabledGames';

const MAX_CLEANUP_MATCHES_PER_RUN = 100;

function getFirstLast(item) {
  const name = (item.displayName || item.email?.split('@')[0] || '').trim();
  if (!name) return { firstName: '—', lastName: '—' };
  const parts = name.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '—' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export default function AdminTools() {
  const { currentUser } = useAuth();
  useRole();
  const navigate = useNavigate();
  const { showAlert, showConfirmPromise, showToast } = useAlert();
  const [loading, setLoading] = useState(true);
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [algoliaSyncRunning, setAlgoliaSyncRunning] = useState(false);
  const [algoliaSyncResult, setAlgoliaSyncResult] = useState(null);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structureResult, setStructureResult] = useState(null);
  const [enabledGamesLoading, setEnabledGamesLoading] = useState(true);
  const [enabledGameIds, setEnabledGameIds] = useState([]);
  const [enabledGamesSaving, setEnabledGamesSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { getDoc } = await import('firebase/firestore');
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const data = userSnap.exists() ? userSnap.data() : {};
        if (!isAdminRole(data) && !data.isSuperAdmin) {
          navigate('/dashboard');
          return;
        }
      } catch (e) {
        if (!cancelled) navigate('/dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    getDoc(doc(db, 'config', CONFIG_ENABLED_GAMES))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        setEnabledGameIds(Array.isArray(data.gameIds) ? data.gameIds : []);
      })
      .catch(() => { if (!cancelled) setEnabledGameIds([]); })
      .finally(() => { if (!cancelled) setEnabledGamesLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser]);

  async function handleSaveEnabledGames() {
    setEnabledGamesSaving(true);
    try {
      await setDoc(doc(db, 'config', CONFIG_ENABLED_GAMES), { gameIds: enabledGameIds || [] });
      showToast('Enabled sports saved. Users will only see selected sports in the game dropdown.', { variant: 'success' });
    } catch (err) {
      console.error('Save enabled games:', err);
      showAlert('Failed to save: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setEnabledGamesSaving(false);
    }
  }

  async function findObsoleteUsers() {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const allUsers = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
    const registeredUserIds = new Set(registrationsSnapshot.docs.map(d => d.data().playerId));
    const matchesSnapshot = await getDocs(collection(db, 'matches'));
    const matchUserIds = new Set();
    matchesSnapshot.docs.forEach(d => {
      const match = d.data();
      if (match.player1Id) matchUserIds.add(match.player1Id);
      if (match.player2Id) matchUserIds.add(match.player2Id);
      if (match.team1Players) match.team1Players.forEach(id => matchUserIds.add(id));
      if (match.team2Players) match.team2Players.forEach(id => matchUserIds.add(id));
      if (match.refereeId) matchUserIds.add(match.refereeId);
    });
    return allUsers.filter(user => {
      if (user.id === currentUser?.uid) return false;
      if (isAdminRole(user)) return false;
      if (registeredUserIds.has(user.id)) return false;
      if (matchUserIds.has(user.id)) return false;
      return true;
    });
  }

  async function findObsoleteMatches() {
    const matchesSnapshot = await getDocs(collection(db, 'matches'));
    const allMatches = matchesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const tournamentsSnapshot = await getDocs(collection(db, 'tournaments'));
    const tournamentIds = new Set(tournamentsSnapshot.docs.map(d => d.id));
    return allMatches.filter(match => {
      if (!match.tournamentId) return true;
      if (!tournamentIds.has(match.tournamentId)) return true;
      return false;
    });
  }

  async function handleSyncUsersToAlgolia() {
    setAlgoliaSyncRunning(true);
    setAlgoliaSyncResult(null);
    try {
      const [usersSnap, membersSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'groupMembers'))
      ]);
      const usersList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const membersList = membersSnap.docs.map(d => d.data());
      const approvedByUser = {};
      membersList.forEach(m => {
        if (m.approved === true && m.userId && m.groupId) {
          if (!approvedByUser[m.userId]) approvedByUser[m.userId] = [];
          approvedByUser[m.userId].push(m.groupId);
        }
      });
      const getApprovedGroupIds = (userId) => approvedByUser[userId] || [];
      const result = await syncAllUsersToAlgolia(usersList, getApprovedGroupIds);
      setAlgoliaSyncResult(result);
      if (result.message) {
        showAlert(result.message, { variant: 'info' });
      } else {
        showToast(`Synced ${result.synced} user(s) to Algolia.${result.errors ? ` Errors: ${result.errors}.` : ''}`, { variant: result.errors ? 'warning' : 'success' });
      }
    } catch (err) {
      console.error('Sync users to Algolia:', err);
      setAlgoliaSyncResult({ synced: 0, errors: 1, message: err?.message || String(err) });
      showAlert('Failed to sync users to Algolia: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setAlgoliaSyncRunning(false);
    }
  }

  async function handleConfirmStructure() {
    setStructureLoading(true);
    setStructureResult(null);
    try {
      const result = await confirmGroupsAndMembershipStructure();
      setStructureResult(result);
      if (result.ok) {
        showToast(`Structure confirmed. Groups: ${result.counts.groups}, groupMembers: ${result.counts.groupMembers}, users: ${result.counts.users}, apartments: ${result.counts.apartments}.`, { variant: 'success' });
      } else {
        showAlert('Confirm structure failed: ' + (result.error || 'Unknown error'), { variant: 'error' });
      }
    } catch (err) {
      setStructureResult({ ok: false, error: err?.message || String(err) });
      showAlert('Confirm structure failed: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setStructureLoading(false);
    }
  }

  async function handleRunMigration() {
    setMigrationRunning(true);
    setMigrationResult(null);
    try {
      const summary = await runApartmentsAndLegacyMigration();
      setMigrationResult(summary);
      const errMsg = summary.errors?.length ? ` Errors: ${summary.errors.length}.` : '';
      const denied = summary.approvedGroupIdsPermissionDenied ?? 0;
      const deniedMsg = denied > 0
        ? ` ${denied} user(s) could not have approvedGroupIds updated (permission denied). Log in as super admin (isAdmin: 'super' or isSuperAdmin: true) or run: node scripts/migrate-apartments-and-legacy.js`
        : '';
      showAlert(
        `Migration complete. Apartments: ${summary.apartmentsProcessed}, groups created: ${summary.groupsCreated}, updated: ${summary.groupsUpdated}. ` +
        `Group members created: ${summary.groupMembersCreated}. approvedGroupIds synced: ${summary.approvedGroupIdsSynced}. Groups memberIds synced: ${summary.groupsMemberIdsSynced ?? 0}.${deniedMsg}${errMsg}`,
        { title: 'Migration complete', variant: 'info' }
      );
    } catch (err) {
      console.error('Migration failed:', err);
      setMigrationResult({ errors: [err?.message || String(err)] });
      showAlert('Migration failed: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setMigrationRunning(false);
    }
  }

  async function handleCleanupDatabase(dryRun = true) {
    setCleaningUp(true);
    setCleanupStatus(null);
    try {
      const obsoleteUsers = await findObsoleteUsers();
      const obsoleteMatches = await findObsoleteMatches();
      const matchesToDelete = obsoleteMatches.slice(0, MAX_CLEANUP_MATCHES_PER_RUN);

      if (dryRun) {
        setCleanupStatus({
          type: 'dryrun',
          obsoleteUsers: obsoleteUsers.length,
          obsoleteMatches: obsoleteMatches.length,
          cappedMatches: Math.min(obsoleteMatches.length, MAX_CLEANUP_MATCHES_PER_RUN),
          users: obsoleteUsers,
          matches: obsoleteMatches
        });
        setCleaningUp(false);
        return;
      }

      const confirmMessage = `Are you sure you want to delete ${obsoleteUsers.length} obsolete users and up to ${matchesToDelete.length} obsolete matches (max ${MAX_CLEANUP_MATCHES_PER_RUN} per run)? This action cannot be undone.`;
      const ok = await showConfirmPromise(confirmMessage, { title: 'Confirm cleanup', destructive: true });
      if (!ok) {
        setCleaningUp(false);
        return;
      }

      let deletedUsers = 0;
      let deletedMatches = 0;
      let errors = 0;

      for (const user of obsoleteUsers) {
        try {
          await deleteDoc(doc(db, 'users', user.id));
          deletedUsers++;
        } catch (err) {
          console.error('Error deleting user', user.id, err);
          errors++;
        }
      }
      for (const match of matchesToDelete) {
        try {
          await deleteVenueBlockForMatch(match);
          await deleteDoc(doc(db, 'matches', match.id));
          deletedMatches++;
        } catch (err) {
          console.error('Error deleting match', match.id, err);
          errors++;
        }
      }

      setCleanupStatus({ type: 'complete', deletedUsers, deletedMatches, errors });
      showAlert(`Cleanup complete!\nDeleted ${deletedUsers} users and ${deletedMatches} matches.\nErrors: ${errors}`, { title: 'Cleanup complete', variant: 'info' });
    } catch (err) {
      console.error('Error during cleanup:', err);
      showAlert('Error during cleanup: ' + err.message, { variant: 'error' });
    } finally {
      setCleaningUp(false);
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-panel-container">
      <header className="admin-header">
        <div>
          <h1 className="page-title">Admin Tools</h1>
          <p className="page-subtitle">Algolia sync, migration, database cleanup (super admin only)</p>
        </div>
        <div className="admin-header-actions">
          <button type="button" onClick={() => navigate('/admin')} className="btn-secondary">
            Admin panel
          </button>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-section card-ui admin-tools-section">
          <h2 className="admin-section-title">Enabled sports / modules</h2>
          <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
            Disable sports that are not yet developed. Only checked sports will appear in the game dropdown and in tournament/group flows. Leave all unchecked to show all sports.
          </p>
          {enabledGamesLoading ? (
            <p style={{ color: '#666' }}>Loading…</p>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginBottom: '16px' }}>
                {GAMES.map((g) => (
                  <label key={g.id} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      checked={(enabledGameIds || []).includes(g.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(enabledGameIds || []), g.id]
                          : (enabledGameIds || []).filter((id) => id !== g.id);
                        setEnabledGameIds(next);
                      }}
                    />
                    <span>{g.icon} {g.name}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                onClick={handleSaveEnabledGames}
                disabled={enabledGamesSaving}
                className="btn-primary"
              >
                {enabledGamesSaving ? 'Saving…' : 'Save enabled sports'}
              </button>
            </>
          )}

          <h2 className="admin-section-title" style={{ marginTop: '24px' }}>Algolia – Users</h2>
          <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
            Index all users into the Algolia &quot;users&quot; index for teammate search (by name) and find-by-email/phone. Each user is synced with their approved group IDs.
          </p>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleSyncUsersToAlgolia}
              disabled={algoliaSyncRunning}
              className="btn-primary"
            >
              {algoliaSyncRunning ? 'Syncing…' : 'Sync users to Algolia'}
            </button>
            {algoliaSyncResult && !algoliaSyncRunning && (
              <span style={{ fontSize: '14px', color: '#666' }}>
                Last run: {algoliaSyncResult.synced} synced{algoliaSyncResult.errors ? `, ${algoliaSyncResult.errors} error(s)` : ''}.
              </span>
            )}
          </div>

          <h2 className="admin-section-title" style={{ marginTop: '24px' }}>Migration – Apartments & legacy data</h2>
          <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
            Ensures app data consistency: creates a <strong>group</strong> for each apartment (same id, type=apartment), creates <strong>groupMembers</strong> for users who have legacy <code>apartmentId</code> or <code>groupId</code> but no groupMembers entry, syncs <code>approvedGroupIds</code> on user docs, and syncs each group&apos;s <code>memberIds</code> and <code>membershipIds</code> from groupMembers. Does not remove or overwrite any existing user or match data. Safe to run multiple times.
          </p>
          <p style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '13px', background: '#fff3cd', padding: '8px', borderRadius: '6px' }}>
            <strong>Syncing approvedGroupIds for all users</strong> requires you to be logged in as a <strong>super admin</strong> (your user doc has <code>isAdmin: &apos;super&apos;</code> or <code>isSuperAdmin: true</code>). Otherwise run the Node script with Firebase Admin: <code>node scripts/migrate-apartments-and-legacy.js</code>
          </p>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleConfirmStructure}
              disabled={structureLoading}
              className="btn-secondary"
            >
              {structureLoading ? 'Checking…' : 'Confirm structure'}
            </button>
            <button
              type="button"
              onClick={handleRunMigration}
              disabled={migrationRunning}
              className="btn-primary"
            >
              {migrationRunning ? 'Running migration…' : 'Migrate apartments & legacy'}
            </button>
            {migrationResult && !migrationRunning && (
              <span style={{ fontSize: '14px', color: '#666' }}>
                Apartments: {migrationResult.apartmentsProcessed}, groups created: {migrationResult.groupsCreated}, group members created: {migrationResult.groupMembersCreated}, approvedGroupIds synced: {migrationResult.approvedGroupIdsSynced}
                {(migrationResult.approvedGroupIdsPermissionDenied ?? 0) > 0 && `, permission denied: ${migrationResult.approvedGroupIdsPermissionDenied}`}
                , groups memberIds synced: {migrationResult.groupsMemberIdsSynced ?? 0}
                {migrationResult.errors?.length ? ` · ${migrationResult.errors.length} error(s)` : ''}.
              </span>
            )}
          </div>
          {structureResult && structureResult.ok && !structureLoading && (
            <div style={{ marginBottom: '24px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', fontSize: '13px', color: '#0c4a6e' }}>
              <strong>Structure confirmed:</strong> groups {structureResult.counts.groups}, groupMembers {structureResult.counts.groupMembers}, users {structureResult.counts.users}, apartments {structureResult.counts.apartments}.
              {structureResult.sampleGroupKeys?.length > 0 && (
                <span> Sample group fields: {structureResult.sampleGroupKeys.join(', ')}.</span>
              )}
            </div>
          )}
          {(migrationResult?.approvedGroupIdsPermissionDenied ?? 0) > 0 && (
            <div style={{ marginBottom: '12px', padding: '12px', background: '#fff3cd', borderRadius: '8px', fontSize: '13px', color: '#856404' }}>
              <strong>{migrationResult.approvedGroupIdsPermissionDenied} user(s)</strong> could not have <code>approvedGroupIds</code> updated (permission denied). Log in as super admin (<code>isAdmin: &apos;super&apos;</code> or <code>isSuperAdmin: true</code> in your user doc) or run: <code>node scripts/migrate-apartments-and-legacy.js</code>
            </div>
          )}
          {migrationResult?.errors?.length > 0 && (
            <details style={{ marginBottom: '24px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: '600', color: '#856404' }}>View errors</summary>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#856404' }}>
                {migrationResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </details>
          )}

          <h2 className="admin-section-title" style={{ marginTop: '32px' }}>Database Cleanup</h2>
          <div style={{ marginBottom: '24px', padding: '16px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
            <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#856404' }}>⚠️ Warning</p>
            <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
              This tool will find and remove obsolete users and matches from the database.
              Obsolete users are those not registered for any tournaments, not involved in any matches, and not admins.
              Obsolete matches are those with invalid or missing tournament IDs.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleCleanupDatabase(true)}
              disabled={cleaningUp}
              className="btn-secondary"
            >
              {cleaningUp ? 'Scanning...' : '🔍 Scan for Obsolete Data'}
            </button>
            {cleanupStatus && cleanupStatus.type === 'dryrun' && (
              <button
                onClick={() => handleCleanupDatabase(false)}
                disabled={cleaningUp}
                className="btn-primary"
                style={{ background: '#dc3545' }}
              >
                {cleaningUp ? 'Deleting...' : `🗑️ Delete ${cleanupStatus.obsoleteUsers} Users & ${cleanupStatus.cappedMatches ?? Math.min(cleanupStatus.obsoleteMatches, MAX_CLEANUP_MATCHES_PER_RUN)} Matches`}
              </button>
            )}
          </div>

          {cleanupStatus && (
            <div style={{ marginTop: '24px' }}>
              {cleanupStatus.type === 'dryrun' && (
                <div style={{ padding: '16px', background: '#e7f3ff', borderRadius: '8px', border: '1px solid #2196F3' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#1976D2' }}>Scan Results (Dry Run)</h3>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Obsolete Users Found:</strong> {cleanupStatus.obsoleteUsers}</p>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Obsolete Matches Found:</strong> {cleanupStatus.obsoleteMatches}</p>
                  {cleanupStatus.obsoleteMatches > MAX_CLEANUP_MATCHES_PER_RUN && (
                    <p style={{ margin: '0 0 8px 0', color: '#856404' }}><strong>Note:</strong> Only the first {MAX_CLEANUP_MATCHES_PER_RUN} matches will be deleted per run. Run again to delete more.</p>
                  )}
                  {cleanupStatus.obsoleteUsers > 0 && (
                    <details style={{ marginTop: '12px' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: '600', marginBottom: '8px' }}>View Obsolete Users</summary>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '8px' }}>
                        <table className="admin-table" style={{ fontSize: '14px' }}>
                          <thead>
                            <tr>
                              <th>First name</th>
                              <th>Last name</th>
                              <th>Email</th>
                              <th>Onboarded</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cleanupStatus.users.map(user => {
                              const { firstName, lastName } = getFirstLast(user);
                              return (
                                <tr key={user.id}>
                                  <td>{firstName}</td>
                                  <td>{lastName}</td>
                                  <td>{user.email || 'N/A'}</td>
                                  <td>{user.onboarded ? 'Yes' : 'No'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                  {cleanupStatus.obsoleteMatches > 0 && (
                    <details style={{ marginTop: '12px' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: '600', marginBottom: '8px' }}>View Obsolete Matches</summary>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '8px' }}>
                        <table className="admin-table" style={{ fontSize: '14px' }}>
                          <thead>
                            <tr>
                              <th>Match ID</th>
                              <th>Tournament ID</th>
                              <th>Status</th>
                              <th>Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cleanupStatus.matches.map(match => (
                              <tr key={match.id}>
                                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{match.id.substring(0, 8)}...</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{match.tournamentId || 'MISSING'}</td>
                                <td>{match.status || 'N/A'}</td>
                                <td>{match.matchType || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                  <p style={{ margin: '16px 0 0 0', fontSize: '14px', color: '#666' }}>
                    Click the delete button above to permanently remove these items from the database.
                  </p>
                </div>
              )}
              {cleanupStatus.type === 'complete' && (
                <div style={{ padding: '16px', background: '#d4edda', borderRadius: '8px', border: '1px solid #28a745' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#155724' }}>✅ Cleanup Complete</h3>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Users Deleted:</strong> {cleanupStatus.deletedUsers}</p>
                  <p style={{ margin: '0 0 8px 0' }}><strong>Matches Deleted:</strong> {cleanupStatus.deletedMatches}</p>
                  {cleanupStatus.errors > 0 && (
                    <p style={{ margin: '8px 0 0 0', color: '#856404' }}><strong>Errors:</strong> {cleanupStatus.errors}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
